// src/pages/Auth/Register.jsx

import {
  useState,
  useEffect,
} from "react";
import {
  Link,
  useNavigate,
} from "react-router-dom";
import {
  Eye,
  Info,
  CheckCircle2,
  XCircle,
} from "lucide-react";

import { supabase } from "../../supabaseClient";
import { getOnboardingCompletionState } from "../../lib/onboarding/profileGate";
import {
  captureReferralCodeFromUrl,
  claimStoredReferralCode,
} from "../../lib/referrals/referrals";

const penguinUrl =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Untitled%20folder/MVP%204/Landing/gwin-penguin.png";

const logoUrl =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Icon/3.png";

// Normalize South African mobile numbers for consistent profile storage.
// This does not verify ownership of the number or register a phone Auth identity.
function normalizeSouthAfricanMobile(value) {
  const cleaned = String(value || "").replace(/[\s().-]/g, "");
  let digits = cleaned;
  if (digits.startsWith("+27")) digits = `0${digits.slice(3)}`;
  else if (digits.startsWith("27")) digits = `0${digits.slice(2)}`;
  if (!/^0[6-8]\d{8}$/.test(digits)) return null;
  return `+27${digits.slice(1)}`;
}

async function destinationForSignedInUser() {
  // Fail closed: incomplete or unavailable status must never bypass onboarding.
  const state = await getOnboardingCompletionState();
  return state.onboardingCompleted ? "/dashboard" : "/profile-gate";
}

const PASSWORD_RULES = [
  {
    key: "length",
    label:
      "At least 8 characters",
    test: (s) =>
      s.length >= 8,
  },
  {
    key: "upper",
    label:
      "At least 1 uppercase letter (A–Z)",
    test: (s) =>
      /[A-Z]/.test(s),
  },
  {
    key: "lower",
    label:
      "At least 1 lowercase letter (a–z)",
    test: (s) =>
      /[a-z]/.test(s),
  },
  {
    key: "number",
    label:
      "At least 1 number (0–9)",
    test: (s) =>
      /\d/.test(s),
  },
  {
    key: "special",
    label:
      "At least 1 special character (!@#$…)",
    test: (s) =>
      /[^A-Za-z0-9]/.test(s),
  },
];

function GoogleIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />

      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />

      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />

      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export default function Register() {
  const [
    formData,
    setFormData,
  ] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [
    showPassword,
    setShowPassword,
  ] = useState(false);

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] = useState(false);

  const [error, setError] =
    useState("");

  const [message, setMessage] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  const navigate = useNavigate();


  useEffect(() => {
    let active = true;
    captureReferralCodeFromUrl();

    async function redirectExistingUser() {
      try {
        const { data, error: authError } = await supabase.auth.getUser();
        if (authError || !data?.user || !active) return;

        try {
          await claimStoredReferralCode(supabase);
        } catch (referralError) {
          console.warn("Referral claim skipped:", referralError);
        }

        let destination = "/profile-gate";
        try {
          destination = await destinationForSignedInUser();
        } catch (statusError) {
          console.warn("Onboarding status unavailable; using gate:", statusError);
        }
        if (active) navigate(destination, { replace: true });
      } catch (authError) {
        console.warn("Could not check current session:", authError);
      }
    }

    redirectExistingUser();
    return () => { active = false; };
  }, [navigate]);

  const handleInputChange = (
    field,
    value
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  function getAuthRedirect() {
    return process.env.NODE_ENV ===
      "development"
      ? "http://localhost:3000/auth/callback"
      : process.env
          .REACT_APP_AUTH_CALLBACK_URL ||
          `${window.location.origin}/auth/callback`;
  }

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    setError("");
    setMessage("");

    const fullName = (
      formData.fullName || ""
    ).trim();

    const email = (
      formData.email || ""
    )
      .trim()
      .toLowerCase();

    const phone = normalizeSouthAfricanMobile(formData.phone);

    const password =
      formData.password || "";

    const confirmPassword =
      formData.confirmPassword || "";

    if (!fullName) {
      setError("Please enter your full name.");
      return;
    }

    if (!phone) {
      setError("Enter a valid South African mobile number, e.g. 0712345678 or +27712345678.");
      return;
    }

    if (
      !email ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        email
      )
    ) {
      setError(
        "Please enter a valid email."
      );

      return;
    }

    const unmet =
      PASSWORD_RULES.filter(
        (rule) =>
          !rule.test(password)
      );

    if (unmet.length > 0) {
      setError(
        "Password must meet all requirements shown below."
      );

      return;
    }

    if (
      password !==
      confirmPassword
    ) {
      setError(
        "Passwords do not match."
      );

      return;
    }

    setLoading(true);

    try {
      const {
        data,
        error: signUpError,
      } =
        await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo:
              getAuthRedirect(),
            data: {
              full_name:
                fullName,
              mobile_number:
                phone,
              phone,
            },
          },
        });

      if (signUpError) {
        setError(
          signUpError.message
        );

        setLoading(false);

        return;
      }

      setLoading(false);

      if (!data.session) {
        setMessage(
          "Account created! Please verify your email using the verification link, then log into Gwin to complete onboarding."
        );

        return;
      }

      const {
        data: { user },
      } =
        await supabase.auth.getUser();

      try {
        await claimStoredReferralCode(
          supabase
        );
      } catch (e) {
        console.warn(
          "Referral claim skipped:",
          e
        );
      }

      let destination = "/profile-gate";
      if (user?.id) {
        try {
          destination = await destinationForSignedInUser();
        } catch (statusError) {
          console.warn("Onboarding status unavailable; using gate:", statusError);
        }
      }
      navigate(destination, { replace: true });
    } catch (err) {
      setLoading(false);

      setError(
        err?.message ||
          "Something went wrong. Please try again."
      );
    }
  };

  const handleGoogleSignup =
    async () => {
      try {
        setError("");
        setMessage("");
        setLoading(true);

        captureReferralCodeFromUrl();

        const redirectTo =
          `${getAuthRedirect()}?intent=signup`;

        const {
          error: oauthError,
        } =
          await supabase.auth.signInWithOAuth(
            {
              provider: "google",
              options: {
                redirectTo,
                queryParams: {
                  prompt:
                    "select_account",
                  access_type:
                    "offline",
                },
              },
            }
          );

        if (oauthError) {
          setError(
            oauthError.message
          );

          setLoading(false);
        }
      } catch (err) {
        setError(
          err?.message ||
            "Unable to start Google signup."
        );

        setLoading(false);
      }
    };

  function InputField({
    label,
    field,
    type = "text",
    autoComplete = "off",
  }) {
    const inputId =
      `register-${field}`;

    return (
      <div>
        <label
          htmlFor={inputId}
          className="mb-2 block text-sm font-extrabold"
        >
          {label}
        </label>

        <input
          id={inputId}
          type={type}
          value={
            formData[field]
          }
          onChange={(event) =>
            handleInputChange(
              field,
              event.target.value
            )
          }
          placeholder={label}
          autoComplete={
            autoComplete
          }
          className="
            min-h-[48px]
            w-full
            rounded-xl
            border
            border-[#D8D8D8]
            bg-white
            px-4
            py-3
            text-sm
            outline-none

            focus:border-[#FF914D]
            focus:ring-2
            focus:ring-[#FF914D]/15
          "
          disabled={loading}
        />
      </div>
    );
  }

  function PasswordField({
    label,
    field,
    show,
    toggle,
  }) {
    const inputId =
      `register-${field}`;

    return (
      <div>
        <label
          htmlFor={inputId}
          className="mb-2 block text-sm font-extrabold"
        >
          {label}
        </label>

        <div className="relative">
          <input
            id={inputId}
            type={
              show
                ? "text"
                : "password"
            }
            value={
              formData[field]
            }
            onChange={(event) =>
              handleInputChange(
                field,
                event.target.value
              )
            }
            placeholder={label}
            className="
              min-h-[48px]
              w-full
              rounded-xl
              border
              border-[#D8D8D8]
              bg-white
              px-4
              py-3
              pr-12
              text-sm
              outline-none

              focus:border-[#FF914D]
              focus:ring-2
              focus:ring-[#FF914D]/15
            "
            autoComplete="new-password"
            disabled={loading}
          />

          <button
            type="button"
            onClick={toggle}
            className="
              absolute
              right-3
              top-1/2
              -translate-y-1/2
              rounded-lg
              p-1.5
            "
            aria-label={
              show
                ? "Hide password"
                : "Show password"
            }
          >
            <Eye className="h-4 w-4 text-[#757575]" />
          </button>
        </div>
      </div>
    );
  }

  function PasswordCriteria({
    password,
  }) {
    return (
      <div
        role="region"
        aria-live="polite"
        className="
          rounded-2xl
          bg-[#F8F8F8]
          p-4
          text-xs
          text-[#555555]
        "
      >
        <div className="mb-3 flex items-center gap-2">
          <Info className="h-4 w-4 shrink-0" />

          <span className="font-extrabold">
            Your password must
            include:
          </span>
        </div>

        <ul className="space-y-2">
          {PASSWORD_RULES.map(
            (rule) => {
              const ok =
                rule.test(
                  password || ""
                );

              return (
                <li
                  key={
                    rule.key
                  }
                  className="flex items-center gap-2"
                >
                  {ok ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 shrink-0 text-[#B5B5B5]" />
                  )}

                  <span
                    className={
                      ok
                        ? "font-semibold text-green-700"
                        : ""
                    }
                  >
                    {
                      rule.label
                    }
                  </span>
                </li>
              );
            }
          )}
        </ul>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col overflow-x-hidden bg-white text-[#111111]">
      {/* HEADER */}
      <header
        className="
          sticky
          top-0
          z-50
          w-full
          border-b
          border-[#EFEFEF]
          bg-white
        "
        style={{
          paddingTop:
            "env(safe-area-inset-top, 0px)",
        }}
      >
        <div
          className="
            mx-auto
            flex
            w-full
            max-w-5xl
            items-center
            justify-between
            gap-2
            px-3
            py-3

            min-[375px]:gap-3
            min-[375px]:px-4

            sm:gap-4
            sm:px-6
            sm:py-4
          "
        >
          <Link
            to="/"
            className="flex min-w-0 shrink-0 items-center"
            aria-label="Gwin home"
          >
            <img
              src={logoUrl}
              alt="Gwin"
              className="
                h-10
                w-auto
                object-contain

                min-[375px]:h-11

                sm:h-12
                md:h-14
              "
              draggable="false"
            />
          </Link>

          <Link
            to="/contact"
            className="
              flex
              min-h-[40px]
              items-center
              justify-center
              rounded-full
              border
              border-[#111111]
              px-3
              py-2
              text-center
              text-[10px]
              font-medium
              leading-tight

              min-[375px]:px-4
              min-[375px]:text-xs

              sm:px-6
              sm:text-sm
            "
          >
            Contact &amp; Legal
            Notices
          </Link>
        </div>
      </header>

      <main className="relative w-full flex-1">
        <div
          className="
            relative
            mx-auto
            flex
            w-full
            max-w-5xl
            flex-col
            px-4
            pb-14

            min-[375px]:px-5
            sm:px-6
          "
        >
                    {/* HERO AREA */}
          <section
            className="
              relative
              z-10
              mx-auto
              w-full
              max-w-[520px]
              px-5
              pt-6
              text-center

              min-[375px]:pt-8

              sm:max-w-[560px]
              sm:px-8
              sm:pt-10

              md:max-w-[620px]
              md:pt-12

              lg:max-w-[680px]
              lg:pt-14
            "
          >
            {/* TOP PENGUIN */}
            <img
              src={penguinUrl}
              alt=""
              aria-hidden="true"
              className="
                pointer-events-none
                absolute
                z-0

                right-[-1%]
                top-[18%]

                w-[41%]

                translate-x-[48%]

                rotate-[-58deg]
                opacity-55
                select-none
              "
              loading="eager"
              decoding="async"
              draggable="false"
            />

            {/* HERO TITLE */}
            <h1
              className="
                relative
                z-10
                mx-auto
                max-w-[460px]

                text-[clamp(1.45rem,6.5vw,2.7rem)]
                font-extrabold
                leading-[1.14]
                tracking-tight

                sm:max-w-[520px]
                md:max-w-[560px]
              "
            >
              You can{" "}
              <span className="text-[#FF914D]">
                build.
              </span>

              <br />

              You can{" "}
              <span className="text-[#FF914D]">
                sell.
              </span>

              <br />

              You can{" "}
              <span className="text-[#FF914D]">
                deliver.
              </span>
            </h1>

            {/* HERO SUBTITLE */}
            <p
              className="
                relative
                z-10
                mx-auto
                mt-6
                max-w-[290px]

                text-[clamp(1.05rem,4.6vw,1.75rem)]
                font-extrabold
                leading-[1.25]
                tracking-tight

                sm:mt-8
                sm:max-w-[330px]

                md:max-w-[360px]
              "
            >
              Now let&apos;s{" "}
              <span className="text-[#FF914D]">
                master finance
              </span>{" "}
              in 5-minutes a day.
            </p>
          </section>
            
          

          {/* REGISTER CARD */}
          <div
            className="
              relative
              z-20
              mx-auto
              mb-16
              mt-6
              w-full
              max-w-[520px]

              sm:mt-8
            "
          >
            <img
  src={penguinUrl}
  alt=""
  aria-hidden="true"
  className="
    pointer-events-none
    absolute
    z-0

    left-[-18%]
    top-[100%]

    w-[55%]

    -translate-x-[48%]
    -translate-y-1/2

    rotate-[62deg]
    opacity-55
    select-none
  "
  loading="eager"
  decoding="async"
  draggable="false"
/>

            <section
              className="
                relative
                z-10
                w-full
                rounded-[22px]
                border
                border-[#DEDEDE]
                bg-white
                p-5
                shadow-[0_2px_8px_rgba(0,0,0,0.03)]

                min-[375px]:p-6

                sm:rounded-[28px]
                sm:p-7
              "
            >
              

              <form
                className="space-y-5"
                onSubmit={
                  handleSubmit
                }
              >
                {error && (
                  <div
                    role="alert"
                    className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-semibold leading-relaxed text-red-700 min-[375px]:text-sm"
                  >
                    {error}
                  </div>
                )}

                {message && (
                  <div
                    role="status"
                    className="
                      rounded-2xl
                      border
                      border-green-200
                      bg-green-50
                      p-4
                      text-xs
                      leading-relaxed
                      text-green-900

                      min-[375px]:text-sm
                    "
                  >
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />

                      <div>
                        <p className="font-extrabold">
                          Almost done —
                          verify your
                          email
                        </p>

                        <p className="mt-2">
                          We&apos;ve
                          sent a
                          verification
                          link to{" "}
                          <strong>
                            {(
                              formData.email ||
                              ""
                            ).trim() ||
                              "your email"}
                          </strong>
                          .
                        </p>

                        <p className="mt-2">
                          Verify your
                          email, then
                          return to Gwin
                          and log in to
                          continue.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {InputField({
                  label:
                    "Full Name",
                  field:
                    "fullName",
                  autoComplete:
                    "name",
                })}

                {InputField({
                  label: "Email",
                  field: "email",
                  type: "email",
                  autoComplete:
                    "email",
                })}

                {InputField({
                  label:
                    "Phone Number (e.g. 0712345678)",
                  field: "phone",
                  type: "tel",
                  autoComplete:
                    "tel",
                })}

                {PasswordField({
                  label:
                    "Password",
                  field:
                    "password",
                  show:
                    showPassword,
                  toggle: () =>
                    setShowPassword(
                      !showPassword
                    ),
                })}

                <PasswordCriteria
                  password={
                    formData.password
                  }
                />

                {PasswordField({
                  label:
                    "Confirm Password",
                  field:
                    "confirmPassword",
                  show:
                    showConfirmPassword,
                  toggle: () =>
                    setShowConfirmPassword(
                      !showConfirmPassword
                    ),
                })}

                <button
                  type="submit"
                  disabled={loading}
                  className="
                    flex
                    min-h-[52px]
                    w-full
                    items-center
                    justify-center
                    rounded-2xl
                    bg-[#FF914D]
                    px-5
                    py-3
                    text-sm
                    font-extrabold
                    text-white
                    transition

                    hover:bg-[#E97D3B]

                    disabled:cursor-not-allowed
                    disabled:opacity-50
                  "
                >
                  {loading
                    ? "Registering..."
                    : "Sign up"}
                </button>

                <div className="flex items-center gap-3 py-1">
                  <div className="h-px flex-1 bg-[#E3E3E3]" />

                  <span className="text-center text-[11px] font-semibold text-[#757575]">
                    Or sign up with
                  </span>

                  <div className="h-px flex-1 bg-[#E3E3E3]" />
                </div>

                <button
                  type="button"
                  onClick={
                    handleGoogleSignup
                  }
                  disabled={loading}
                  aria-disabled={
                    loading
                  }
                  className="
                    flex
                    min-h-[50px]
                    w-full
                    items-center
                    justify-center
                    gap-3
                    rounded-2xl
                    border
                    border-[#D8D8D8]
                    bg-white
                    px-4
                    py-3
                    text-sm
                    font-bold
                    transition

                    hover:bg-[#F8F8F8]

                    disabled:opacity-50
                  "
                >
                  <GoogleIcon />

                  Continue with
                  Google
                </button>

                <p className="text-center text-xs font-medium leading-relaxed min-[375px]:text-sm">
                  Already signed up?{" "}
                  <Link
                    to="/login"
                    replace
                    className="font-extrabold text-[#FF914D]"
                  >
                  Login here
                </Link>
              </p>
              </form>
            </section>
          </div>

          
        </div>
      </main>
    </div>
  );
}